import {
  getEffectiveStatus,
  getReleasePhaseIcon,
  normalizeStatus
} from 'utils/helpers';
import { RISK_LEVELS, STATUS_VALUES } from 'utils/constants';
import type { MonthlyTimelineItem } from './types';

export function isEventLane(laneLabel: string) {
  return laneLabel.toLowerCase().includes('event');
}

export function isKeyDateLane(laneLabel: string) {
  return laneLabel.toLowerCase().includes('key date');
}

export function getItemRevenueImpact(item: MonthlyTimelineItem): boolean {
  if (item.revenueImpact === true) return true;
  if (item.generalInfo?.revenueImpact === true) return true;

  const releasePhases = item.releasePhases;
  if (!releasePhases || !Array.isArray(releasePhases)) return false;

  if (item.releasePhaseId) {
    const currentPhase = releasePhases.find(
      phase => phase.id === item.releasePhaseId
    );
    return currentPhase?.generalInfo?.revenueImpact === true;
  }

  if (item.releasePhase) {
    const currentPhase = releasePhases.find(
      phase => phase.phaseName === item.releasePhase
    );
    return currentPhase?.generalInfo?.revenueImpact === true;
  }

  return false;
}

function hasCompletedGAOrPublicBeta(item: MonthlyTimelineItem): boolean {
  const releasePhases = item.releasePhases;
  if (!releasePhases || !Array.isArray(releasePhases)) return false;

  return releasePhases.some(phase => {
    const phaseName = phase.phaseName?.toLowerCase() || '';
    return (
      (phaseName === 'ga' || phaseName === 'public beta') &&
      phase.status === STATUS_VALUES.COMPLETE
    );
  });
}

export function isItemClosed(item: MonthlyTimelineItem): boolean {
  const effectiveStatus = getEffectiveStatus(
    item.status,
    item.showOnRoadmap,
    item.showOnTimeline,
    item.secondaryDateStatus
  );
  const normalizedStatus = normalizeStatus(effectiveStatus);

  return (
    normalizedStatus === STATUS_VALUES.COMPLETE ||
    normalizedStatus === STATUS_VALUES.CLOSED ||
    hasCompletedGAOrPublicBeta(item)
  );
}

export function hasValidRiskLevel(riskLevel?: string): boolean {
  if (!riskLevel || riskLevel === RISK_LEVELS.NA) return false;

  return (
    riskLevel === RISK_LEVELS.LOW ||
    riskLevel === RISK_LEVELS.MODERATE ||
    riskLevel === RISK_LEVELS.HIGH ||
    riskLevel === RISK_LEVELS.IN_PLANNING
  );
}

export function countMonthlyItemIcons(
  item: MonthlyTimelineItem,
  laneLabel: string
): number {
  if (isEventLane(laneLabel) || isKeyDateLane(laneLabel)) return 0;

  let count = 0;
  if (getItemRevenueImpact(item)) count += 1;

  const phaseIcon = getReleasePhaseIcon(item.releasePhase);
  if (item.releasePhase && phaseIcon) count += 1;

  const products = item.products;
  const hasProducts = Array.isArray(products)
    ? products.length > 0
    : Boolean(products);
  if (hasProducts) count += 1;

  if (hasValidRiskLevel(item.riskLevel)) {
    count += 1;
  }

  return count;
}
